/**
 * Per-field metadata for client sections — the field half of the data dictionary.
 * SINGLE SOURCE OF TRUTH of every client field, on every surface (ADR 0004).
 *
 * A `FieldDescriptor` describes the DATA, not the UI: its dot-path, the canonical label,
 * the portal's conversational copy when it differs, a SEMANTIC `kind` (what the value
 * means), and who it belongs to (`clientTypes` / `audiences`). It deliberately carries no
 * formatter closure and no React control: those depend on app-level catalogs (countries,
 * enum labels) that must not leak into this framework-free package.
 *
 * Each consumer PROJECTS from here — the projections live app-side, next to the catalogs
 * they need:
 *  - Review (web): `kind` → a concrete read-only formatter (`review/field-kind-format.ts`).
 *  - Forms (web): `kind` → a concrete control; label from `portalLabel ?? label`.
 *
 * Layout is NOT here on purpose: grids, step order and UX conditionals stay hand-built in
 * each surface's JSX. The dictionary owns the DATA; the surface owns how it looks.
 *
 * Kept honest by `api/client-portal/domain/portal-registry-zod-sync.spec.ts`, which walks
 * every path here and fails if it does not resolve against the section's Zod schema.
 *
 * AGREGAR UN CAMPO NUEVO toca once archivos en cinco capas, y varias de las trampas no las ve
 * el typecheck (el sandbox de Mongoose lo descarta callado; hay DOS registros de etiquetas de
 * enum y registrarlo en uno solo pinta el código crudo; los niveles progresivos sólo prenden y
 * apagan claves de primer nivel). El recorrido completo, con el porqué de cada paso, está en
 * `docs/architecture/client-field-checklist.md`.
 */
import type { PortalClientType, PortalSectionId } from './sections'

/**
 * Semantic value kind. A finite vocabulary every consumer understands. `enum:<key>`
 * defers the label map to the consumer (key matches an `@idc-forms/types` label map).
 */
export type FieldKind =
  | 'text'
  | 'number'
  | 'boolean'
  | 'date'
  | 'money'
  | 'country'
  | 'department' // Guatemalan department numeric code → catalog name
  | 'municipality' // Guatemalan municipality numeric code → catalog name (needs its department to disambiguate)
  | 'nationality' // array of country iso codes
  | 'fullName' // composed from name/lastName subdocuments
  | 'enum' // uses `enumKey` to resolve the label map
  | 'profession' // resolved against the professions catalog (runtime ctx)
  | 'purpose' // negotiation purpose + `otherPurpose` fallback
  | 'association' // PEP partner association reason catalog
  | 'originWealth' // array of origin-of-wealth catalog values
  | 'sourceFund' // juridical source-of-funds catalog + `otherType` fallback
  | 'modification' // deed/document modification type catalog (juridical constitution)

/**
 * Surface a field is shown on. Omitting `audiences` on a descriptor means BOTH — the
 * common case, and the reason the tag is opt-in.
 */
export type FieldAudience = 'portal' | 'internal'

/** A single field's metadata. `path` is the dot-path inside the section sandbox. */
export interface FieldDescriptor {
  path: string
  /**
   * CANONICAL name of the field — what the advisor reads: review, internal detail and the
   * internal form. Never the conversational copy; that is `portalLabel`.
   */
  label: string
  /**
   * Copy aimed at the client filling the public portal, when it differs from `label`.
   * Omit when both audiences say the same thing. The portal resolves `portalLabel ?? label`.
   *
   * The divergence is deliberate: the portal asks "¿A qué te dedicas?" where review says
   * "Profesión u oficio". What gets unified is the SOURCE of the copy, not the text.
   */
  portalLabel?: string
  kind: FieldKind
  /** For `kind: 'enum'`, the label-map key (e.g. 'gender', 'civilStatus', 'migration'). */
  enumKey?: string
  /**
   * Person types this field belongs to. Omit = applies to all. Used to prune fields
   * that only exist for one person type (e.g. equity spouse → individual only).
   */
  clientTypes?: readonly PortalClientType[]
  /**
   * Surfaces this field belongs to. Omit = both. Exact mirror of how `clientTypes` prunes
   * by person type: `['internal']` keeps a field out of the public portal (the
   * administrative dossier — agency, contract dates — is the whole reason this exists).
   */
  audiences?: readonly FieldAudience[]
}

/**
 * A titled group of scalar fields (mirrors the design's FieldSets).
 *
 * Named `...Spec` on purpose: `FieldGroup` is also a shadcn layout component in the web
 * (`@/shared/ui/field`, 117 usages). Two different `FieldGroup`s in scope is a bug waiting
 * for an auto-import to happen.
 */
export interface FieldGroupSpec {
  title: string
  fields: FieldDescriptor[]
}

/** An iterable array of records inside a section (e.g. PEP persons, bank accounts). */
export interface ArrayFieldSpec {
  /** Dot-path of the array within the section (e.g. `pep`, `assets.banks`). */
  path: string
  /** Singular item title (e.g. "Persona PEP"). */
  itemTitle: string
  fields: FieldDescriptor[]
}

/** A section's full field metadata. */
export interface SectionFields {
  scalarGroups?: FieldGroupSpec[]
  arrays?: ArrayFieldSpec[]
}

// ── Reusable field clusters ──────────────────────────────────────────────────

/** Public-position fields shared by PEP person/relative/partner records. */
const PUBLIC_POSITION: FieldDescriptor[] = [
  { path: 'institutionWork', label: 'Institución donde trabaja', kind: 'text' },
  { path: 'position', label: 'Puesto que desempeña', kind: 'text' },
  { path: 'country', label: 'País de la entidad', kind: 'country' }
]

// ── Section field catalogs ───────────────────────────────────────────────────

const APPLICANT_FIELDS: SectionFields = {
  scalarGroups: [
    {
      title: 'Nombres y apellidos',
      fields: [
        { path: 'name.first', label: 'Primer nombre', kind: 'text' },
        { path: 'name.second', label: 'Segundo nombre', kind: 'text' },
        { path: 'name.others', label: 'Otros nombres', kind: 'text' },
        { path: 'lastName.first', label: 'Primer apellido', kind: 'text' },
        { path: 'lastName.second', label: 'Segundo apellido', kind: 'text' },
        { path: 'lastName.married', label: 'Apellido de casada', kind: 'text' }
      ]
    },
    {
      title: 'Documento de identificación',
      fields: [
        {
          path: 'identificationCard.type',
          label: 'Tipo de documento',
          kind: 'enum',
          enumKey: 'identification'
        },
        {
          path: 'identificationCard.number',
          label: 'Número de documento',
          kind: 'text'
        },
        {
          path: 'identificationCard.dateDue',
          label: 'Vigencia del documento',
          portalLabel: 'Fecha de vencimiento',
          kind: 'date'
        },
        { path: 'identificationCard.emission.country', label: 'País de emisión', kind: 'country' }
      ]
    },
    {
      title: 'Información personal',
      fields: [
        { path: 'birthdate', label: 'Fecha de nacimiento', kind: 'date' },
        { path: 'profession', label: 'Profesión u oficio', kind: 'profession' },
        // Texto libre que el cliente declara cuando elige la profesión "Otra". Sin este
        // descriptor el asesor sólo veía "Otra" y aprobaba a ciegas un dato de riesgo
        // KYC/PLD. Condicional: su visibilidad la resuelve el consumidor (el diccionario
        // es metadata pura) — ver APPLICANT_FIELD_VISIBILITY en el review.
        {
          path: 'otherProfession',
          label: 'Otra profesión u oficio',
          portalLabel: '¿A qué te dedicas?',
          kind: 'text'
        },
        { path: 'gender', label: 'Género', kind: 'enum', enumKey: 'gender' },
        { path: 'civilStatus', label: 'Estado civil', kind: 'enum', enumKey: 'civilStatus' },
        { path: 'nit', label: 'NIT', kind: 'text' },
        {
          path: 'economicActivityRTU',
          label: 'Actividad económica (RTU)',
          kind: 'text'
        }
      ]
    },
    {
      title: 'Lugar de nacimiento y nacionalidad',
      fields: [
        { path: 'birthplace.country', label: 'País de nacimiento', kind: 'country' },
        { path: 'birthplace.department', label: 'Departamento de nacimiento', kind: 'department' },
        { path: 'birthplace.municipality', label: 'Municipio de nacimiento', kind: 'municipality' },
        { path: 'nationality', label: 'Nacionalidad', kind: 'nationality' },
        {
          path: 'migrationStatus',
          label: 'Estatus migratorio',
          portalLabel: 'Condición migratoria',
          kind: 'enum',
          enumKey: 'migration'
        },
        // Mismo caso que `otherProfession`: el detalle que el cliente escribe cuando su
        // condición migratoria es "Otra". Label reusado del descriptor que ya existe para
        // el representante legal, para no divergir (el motivo del ADR 0004).
        {
          path: 'otherMigrationStatus',
          label: 'Otra condición migratoria',
          portalLabel: 'Especifica tu condición migratoria',
          kind: 'text'
        }
      ]
    },
    {
      title: 'Residencia',
      fields: [
        { path: 'residence.country', label: 'País de residencia', kind: 'country' },
        { path: 'residence.department', label: 'Departamento de residencia', kind: 'department' },
        { path: 'residence.municipality', label: 'Municipio de residencia', kind: 'municipality' },
        { path: 'residence.address', label: 'Dirección', kind: 'text' },
        { path: 'residence.zone', label: 'Zona', kind: 'number' },
        // Tenencia de la vivienda. Va en el diccionario del SOLICITANTE y no en el del
        // representante legal: la pregunta es por dónde vive el titular del expediente.
        // Titularidad de la vivienda + su justificación. Van en el diccionario del SOLICITANTE
        // y no en el del representante legal: la pregunta es por dónde vive el titular.
        //
        // El par sigue el molde de `ownAct`: etiqueta canónica en `label` —la que lee el asesor
        // en la revisión— y la conversacional en `portalLabel`. La justificación sólo se pide
        // cuando la respuesta es NO; la regla vive en `IndividualApplicant.forLevel`.
        {
          path: 'residence.isInOwnName',
          label: 'Recibo a su nombre',
          portalLabel: '¿El recibo de la vivienda donde vives está a tu nombre?',
          kind: 'boolean'
        },
        {
          path: 'residence.ownershipJustification',
          label: 'Justificación',
          portalLabel: 'Justifica por qué no está a tu nombre',
          kind: 'text'
        }
      ]
    },
    {
      title: 'Contacto',
      fields: [
        { path: 'email', label: 'Correo electrónico', kind: 'text' },
        { path: 'phone.0', label: 'Teléfono principal', kind: 'text' }
      ]
    },
    {
      title: 'Declaración',
      fields: [
        {
          path: 'ownAct',
          label: 'Actúa en nombre propio',
          portalLabel: '¿Actúas en nombre propio?',
          kind: 'boolean'
        }
      ]
    }
  ]
}

const PEP_FIELDS: SectionFields = {
  scalarGroups: [
    {
      title: 'Resumen',
      fields: [
        { path: 'isPep', label: '¿Es PEP?', kind: 'boolean' },
        { path: 'hasRelativesPep', label: '¿Familiares PEP?', kind: 'boolean' },
        { path: 'hasPartnersPep', label: '¿Socios/asociados PEP?', kind: 'boolean' },
        { path: 'isCPE', label: '¿Contratista del Estado?', kind: 'boolean' }
      ]
    }
  ],
  arrays: [
    {
      path: 'pep',
      itemTitle: 'Persona PEP',
      fields: [
        ...PUBLIC_POSITION,
        {
          path: 'originWealth',
          label: 'Origen de la riqueza',
          portalLabel: 'Origen o procedencia de la riqueza',
          kind: 'originWealth'
        },
        {
          path: 'otherOriginWealth',
          label: 'Otro origen',
          portalLabel: 'Especifica el otro origen de riqueza',
          kind: 'text'
        }
      ]
    },
    {
      path: 'relativesPep',
      itemTitle: 'Familiar PEP',
      fields: [
        { path: 'name.first', label: 'Nombre', kind: 'fullName' },
        { path: 'relationship', label: 'Parentesco', kind: 'enum', enumKey: 'relationship' },
        {
          path: 'otherRelationship',
          label: 'Otro parentesco',
          portalLabel: 'Especifica el parentesco',
          kind: 'text'
        },
        { path: 'gender', label: 'Género', kind: 'enum', enumKey: 'gender' },
        {
          path: 'migrationStatus',
          label: 'Condición migratoria',
          kind: 'enum',
          enumKey: 'migration'
        },
        ...PUBLIC_POSITION
      ]
    },
    {
      path: 'partnersPep',
      itemTitle: 'Socio/asociado PEP',
      fields: [
        { path: 'name.first', label: 'Nombre', kind: 'fullName' },
        { path: 'association', label: 'Motivo de asociación', kind: 'association' },
        {
          path: 'otherAssociation',
          label: 'Otra asociación',
          portalLabel: 'Especifica el tipo de asociación',
          kind: 'text'
        },
        { path: 'gender', label: 'Género', kind: 'enum', enumKey: 'gender' },
        {
          path: 'migrationStatus',
          label: 'Condición migratoria',
          kind: 'enum',
          enumKey: 'migration'
        },
        ...PUBLIC_POSITION
      ]
    }
  ]
}

const ECONOMIC_FIELDS: SectionFields = {
  scalarGroups: [
    {
      title: 'Resumen',
      fields: [
        {
          path: 'purpose',
          label: 'Propósito de la negociación',
          portalLabel: '¿Para qué destinarás esta inversión inmobiliaria?',
          kind: 'purpose'
        }
      ]
    }
  ],
  arrays: [
    {
      path: 'dependencyRelations',
      itemTitle: 'Relación de dependencia',
      fields: [
        { path: 'company', label: 'Empresa', portalLabel: 'Nombre de la empresa', kind: 'text' },
        { path: 'position', label: 'Puesto', portalLabel: 'Puesto que desempeña', kind: 'text' },
        { path: 'seniority', label: 'Antigüedad', portalLabel: 'Antigüedad laboral', kind: 'text' }
      ]
    },
    {
      path: 'ownBusinesses',
      itemTitle: 'Negocio propio',
      fields: [
        { path: 'name', label: 'Nombre del negocio', kind: 'text' },
        {
          path: 'activity',
          label: 'Actividad',
          portalLabel: 'Actividad u objeto del negocio',
          kind: 'text'
        }
      ]
    },
    {
      path: 'otherIncomes',
      itemTitle: 'Otra fuente de ingreso',
      fields: [
        { path: 'type', label: 'Tipo', portalLabel: 'Tipo de ingreso', kind: 'text' },
        { path: 'detail', label: 'Detalle', kind: 'text' }
      ]
    },
    {
      path: 'monthlyIncomes',
      itemTitle: 'Ingreso mensual',
      fields: [{ path: 'amount', label: 'Monto', portalLabel: 'Monto mensual', kind: 'money' }]
    },
    {
      path: 'monthlyExpenses',
      itemTitle: 'Egreso mensual',
      fields: [{ path: 'amount', label: 'Monto', portalLabel: 'Monto mensual', kind: 'money' }]
    }
  ]
}

/**
 * Información económica de la persona JURÍDICA. NO es una poda de la individual: son
 * paths y conceptos distintos (entidad: relación de negocios, tamaño, proveedores,
 * clientes, fuentes de fondos, montos por moneda). Espejo del FEIC interno.
 */
const ECONOMIC_JURIDICAL_FIELDS: SectionFields = {
  scalarGroups: [
    {
      title: 'Perfil de la entidad',
      fields: [
        {
          path: 'businessRelationship',
          label: 'Propósito de la relación de negocios',
          kind: 'text'
        },
        { path: 'numberEmployees', label: 'No. estimado de empleados', kind: 'number' },
        {
          path: 'numberOffices',
          label: 'No. de subsidiarias / agencias / oficinas',
          kind: 'number'
        }
      ]
    }
  ],
  arrays: [
    {
      path: 'suppliers',
      itemTitle: 'Proveedor',
      fields: [
        { path: 'name', label: 'Nombre', kind: 'text' },
        { path: 'country', label: 'País', kind: 'country' }
      ]
    },
    {
      path: 'clients',
      itemTitle: 'Cliente',
      fields: [
        { path: 'name', label: 'Nombre', kind: 'text' },
        { path: 'country', label: 'País', kind: 'country' }
      ]
    },
    {
      path: 'sourceFunds',
      itemTitle: 'Fuente de fondos',
      fields: [
        { path: 'type', label: 'Fuente', kind: 'sourceFund' },
        {
          path: 'otherType',
          label: 'Otra fuente (detalle)',
          portalLabel: 'Especifica la fuente',
          kind: 'text'
        },
        { path: 'description', label: 'Descripción', kind: 'text' },
        { path: 'receivesStateFunds', label: '¿Recibe fondos del Estado?', kind: 'boolean' }
      ]
    },
    {
      path: 'monthlyIncomes',
      itemTitle: 'Ingreso mensual',
      fields: [
        { path: 'currency', label: 'Moneda', kind: 'text' },
        { path: 'amount', label: 'Monto', portalLabel: 'Monto mensual', kind: 'money' }
      ]
    },
    {
      path: 'monthlyExpenses',
      itemTitle: 'Egreso mensual',
      fields: [
        { path: 'currency', label: 'Moneda', kind: 'text' },
        { path: 'amount', label: 'Monto', portalLabel: 'Monto mensual', kind: 'money' }
      ]
    }
  ]
}

const EQUITY_FIELDS: SectionFields = {
  scalarGroups: [
    {
      // Cónyuge: sólo aplica a la persona individual (una empresa no lo tiene). Todo el
      // grupo se poda para jurídica (pruneForClientType descarta el grupo vacío).
      title: 'Cónyuge',
      fields: [
        {
          path: 'hasSpouse',
          label: '¿Tiene cónyuge?',
          portalLabel: '¿Tienes cónyuge?',
          kind: 'boolean',
          clientTypes: ['individual']
        },
        {
          path: 'spouseIncludedInThePurchase',
          label: '¿Participa en esta compra?',
          portalLabel: '¿Tu cónyuge participa en esta compra?',
          kind: 'boolean',
          clientTypes: ['individual']
        },
        {
          path: 'spouse.name',
          label: 'Cónyuge',
          portalLabel: 'Nombre del cónyuge',
          kind: 'text',
          clientTypes: ['individual']
        },
        {
          path: 'spouse.companyWork',
          label: 'Empresa donde trabaja',
          kind: 'text',
          clientTypes: ['individual']
        },
        {
          path: 'spouse.monthlyIncome',
          label: 'Ingreso del cónyuge',
          portalLabel: 'Ingreso mensual',
          kind: 'money',
          clientTypes: ['individual']
        }
      ]
    },
    {
      title: 'Gastos mensuales',
      fields: [
        { path: 'expenses.rentHouse', label: 'Alquiler / vivienda', kind: 'money' },
        { path: 'expenses.loans', label: 'Préstamos', kind: 'money' },
        { path: 'expenses.vehicles', label: 'Vehículos', kind: 'money' },
        { path: 'expenses.creditCard', label: 'Tarjetas de crédito', kind: 'money' },
        { path: 'expenses.waterElectricityPhone', label: 'Agua, luz y teléfono', kind: 'money' },
        { path: 'expenses.collegiate', label: 'Colegios / educación', kind: 'money' },
        { path: 'expenses.food', label: 'Alimentación', kind: 'money' },
        { path: 'expenses.transport', label: 'Transporte', kind: 'money' },
        { path: 'expenses.insurance', label: 'Seguros', kind: 'money' },
        { path: 'expenses.recreation', label: 'Recreación', kind: 'money' },
        { path: 'expenses.other.description', label: 'Otro gasto (descripción)', kind: 'text' },
        { path: 'expenses.other.amount', label: 'Otro gasto (monto)', kind: 'money' }
      ]
    },
    {
      title: 'Activos',
      fields: [
        {
          path: 'assets.cash',
          label: 'Efectivo',
          portalLabel: 'Efectivo disponible',
          kind: 'money'
        },
        { path: 'assets.household', label: 'Menaje de casa', kind: 'money' },
        { path: 'assets.furnitureEquipment', label: 'Mobiliario y equipo', kind: 'money' },
        { path: 'assets.accountsReceivable', label: 'Cuentas por cobrar', kind: 'money' },
        { path: 'assets.other.description', label: 'Otro activo (descripción)', kind: 'text' },
        { path: 'assets.other.amount', label: 'Otro activo (monto)', kind: 'money' }
      ]
    }
  ],
  arrays: [
    {
      path: 'assets.banks',
      itemTitle: 'Cuenta bancaria',
      fields: [
        { path: 'name', label: 'Banco', portalLabel: 'Institución financiera', kind: 'text' },
        { path: 'number', label: 'Número de cuenta', kind: 'text' },
        { path: 'type', label: 'Tipo', portalLabel: 'Tipo de cuenta', kind: 'text' },
        { path: 'currency', label: 'Moneda', kind: 'text' },
        { path: 'balance', label: 'Saldo', kind: 'money' }
      ]
    },
    {
      path: 'assets.stocksBonds',
      itemTitle: 'Inversión',
      fields: [
        { path: 'class', label: 'Clase', kind: 'text' },
        { path: 'quantity', label: 'Cantidad', kind: 'number' },
        { path: 'identification', label: 'Identificación', kind: 'text' },
        { path: 'nominalValue', label: 'Valor nominal', kind: 'money' },
        { path: 'marketValue', label: 'Valor comercial', kind: 'money' }
      ]
    },
    {
      path: 'assets.vehicles',
      itemTitle: 'Vehículo',
      fields: [
        { path: 'brand', label: 'Marca', kind: 'text' },
        { path: 'line', label: 'Línea', kind: 'text' },
        { path: 'model', label: 'Modelo', kind: 'text' },
        {
          path: 'marketValue',
          label: 'Valor de mercado',
          portalLabel: 'Valor comercial',
          kind: 'money'
        },
        { path: 'warranty', label: '¿En garantía de préstamo?', kind: 'boolean' },
        { path: 'term', label: 'Plazo', kind: 'text' }
      ]
    },
    {
      path: 'assets.realEstates',
      itemTitle: 'Inmueble',
      fields: [
        { path: 'description', label: 'Descripción', portalLabel: 'Dirección', kind: 'text' },
        { path: 'type', label: 'Tipo', portalLabel: 'Tipo de inmueble', kind: 'text' },
        {
          path: 'marketValue',
          label: 'Valor de mercado',
          portalLabel: 'Valor comercial',
          kind: 'money'
        },
        { path: 'mortgager', label: '¿Hipotecado?', kind: 'boolean' },
        { path: 'term', label: 'Plazo', kind: 'text' }
      ]
    },
    {
      path: 'liabilities.loans',
      itemTitle: 'Préstamo',
      fields: [
        { path: 'bank', label: 'Banco', portalLabel: 'Banco / acreedor', kind: 'text' },
        {
          path: 'destination',
          label: 'Destino',
          portalLabel: 'Destino del préstamo',
          kind: 'text'
        },
        { path: 'dateConsession', label: 'Fecha de concesión', kind: 'date' },
        { path: 'initValue', label: 'Monto original', kind: 'money' },
        { path: 'currentBalance', label: 'Saldo actual', kind: 'money' }
      ]
    },
    {
      path: 'liabilities.creditCards',
      itemTitle: 'Tarjeta de crédito',
      fields: [
        { path: 'emitter', label: 'Emisor', kind: 'text' },
        { path: 'limitedGranted', label: 'Límite otorgado', kind: 'money' },
        { path: 'currentBalance', label: 'Saldo actual', kind: 'money' },
        { path: 'paymentAgreement', label: '¿Convenio de pago?', kind: 'boolean' }
      ]
    },
    {
      path: 'liabilities.commercialCompanies',
      itemTitle: 'Deuda comercial',
      fields: [
        { path: 'commercialCompany', label: 'Casa comercial', kind: 'text' },
        { path: 'destination', label: 'Destino', kind: 'text' },
        { path: 'dateConsession', label: 'Fecha de concesión', kind: 'date' },
        { path: 'initValue', label: 'Monto original', kind: 'money' },
        { path: 'currentBalance', label: 'Saldo actual', kind: 'money' }
      ]
    },
    {
      path: 'liabilities.otherPayables',
      itemTitle: 'Cuenta por pagar',
      fields: [
        { path: 'description', label: 'Descripción', kind: 'text' },
        { path: 'destination', label: 'Destino', kind: 'text' },
        { path: 'dateConsession', label: 'Fecha de concesión', kind: 'date' },
        { path: 'initValue', label: 'Monto original', kind: 'money' },
        { path: 'currentBalance', label: 'Saldo actual', kind: 'money' }
      ]
    },
    {
      path: 'sharesCompanies',
      itemTitle: 'Participación en empresa',
      fields: [
        { path: 'company', label: 'Empresa', kind: 'text' },
        {
          path: 'percentage',
          label: 'Porcentaje',
          portalLabel: '% de participación',
          kind: 'number'
        },
        { path: 'nit', label: 'NIT', kind: 'text' }
      ]
    }
  ]
}

const LEGAL_REPRESENTATIVES_FIELDS: SectionFields = {
  arrays: [
    {
      path: 'representatives',
      itemTitle: 'Representante',
      fields: [
        { path: 'name.first', label: 'Nombre completo', kind: 'fullName' },
        { path: 'qualityRepresentative', label: 'Calidad con la que actúa', kind: 'text' },
        {
          path: 'identificationCard.type',
          label: 'Tipo de documento',
          kind: 'enum',
          enumKey: 'identification'
        },
        { path: 'identificationCard.number', label: 'Número de documento', kind: 'text' },
        { path: 'identificationCard.dateDue', label: 'Vigencia del documento', kind: 'date' },
        { path: 'identificationCard.emission.country', label: 'País de emisión', kind: 'country' },
        { path: 'birthdate', label: 'Fecha de nacimiento', kind: 'date' },
        { path: 'birthplace.country', label: 'País de nacimiento', kind: 'country' },
        { path: 'birthplace.department', label: 'Departamento de nacimiento', kind: 'department' },
        { path: 'birthplace.municipality', label: 'Municipio de nacimiento', kind: 'municipality' },
        { path: 'gender', label: 'Género', kind: 'enum', enumKey: 'gender' },
        { path: 'civilStatus', label: 'Estado civil', kind: 'enum', enumKey: 'civilStatus' },
        { path: 'profession', label: 'Profesión', kind: 'text' },
        { path: 'otherProfession', label: 'Otra profesión u oficio', kind: 'text' },
        { path: 'nationality', label: 'Nacionalidad(es)', kind: 'nationality' },
        {
          path: 'migrationStatus',
          label: 'Condición migratoria',
          kind: 'enum',
          enumKey: 'migration'
        },
        { path: 'otherMigrationStatus', label: 'Otra condición migratoria', kind: 'text' },
        { path: 'nit', label: 'NIT', kind: 'text' },
        { path: 'economicActivityRTU', label: 'Actividad económica (RTU)', kind: 'text' },
        { path: 'residence.country', label: 'País de residencia', kind: 'country' },
        { path: 'residence.department', label: 'Departamento de residencia', kind: 'department' },
        { path: 'residence.municipality', label: 'Municipio de residencia', kind: 'municipality' },
        { path: 'residence.address', label: 'Dirección de residencia', kind: 'text' },
        { path: 'residence.zone', label: 'Zona', kind: 'number' },
        { path: 'isPep', label: '¿Es PEP?', kind: 'boolean' },
        { path: 'pep', label: 'Detalle PEP', kind: 'text' },
        { path: 'hasRelativesPep', label: '¿Tiene familiares PEP?', kind: 'boolean' },
        { path: 'relativesPep', label: 'Familiares PEP', kind: 'text' },
        { path: 'hasPartnersPep', label: '¿Tiene socios/asociados PEP?', kind: 'boolean' },
        { path: 'partnersPep', label: 'Socios/asociados PEP', kind: 'text' },
        { path: 'isCPE', label: '¿Es contratista o proveedor del Estado?', kind: 'boolean' },
        { path: 'email', label: 'Correo', kind: 'text' },
        { path: 'phone', label: 'Teléfono(s)', kind: 'text' },
        // Nombramiento — SÓLO jurídica: registro (Registro Mercantil), cargo, plazo, vigencia
        // y notario. El mandato individual no tiene nada de esto, así que se poda del portal
        // (form, review y detector de faltantes) igual que el cónyuge.
        {
          path: 'powerRegister.name',
          label: 'Registro del poder',
          portalLabel: 'Registro donde se inscribió',
          kind: 'text',
          clientTypes: ['juridical']
        },
        { path: 'powerRegister.number', label: 'Número', kind: 'text', clientTypes: ['juridical'] },
        { path: 'powerRegister.sheet', label: 'Folio', kind: 'text', clientTypes: ['juridical'] },
        { path: 'powerRegister.book', label: 'Libro', kind: 'text', clientTypes: ['juridical'] },
        {
          path: 'positionAppointment',
          label: 'Cargo para el que se le nombró',
          kind: 'text',
          clientTypes: ['juridical']
        },
        {
          path: 'termAppointment',
          label: 'Plazo del nombramiento',
          kind: 'enum',
          enumKey: 'termAppointment',
          clientTypes: ['juridical']
        },
        { path: 'initialDate', label: 'Fecha inicial', kind: 'date', clientTypes: ['juridical'] },
        { path: 'finalDate', label: 'Fecha final', kind: 'date', clientTypes: ['juridical'] },
        {
          path: 'notary.name',
          label: 'Notario',
          portalLabel: 'Notario que autorizó el nombramiento',
          kind: 'text',
          clientTypes: ['juridical']
        },
        {
          path: 'notary.gender',
          label: 'Género del notario',
          kind: 'enum',
          enumKey: 'gender',
          clientTypes: ['juridical']
        }
      ]
    }
  ]
}

const COMPANY_FIELDS: SectionFields = {
  scalarGroups: [
    {
      title: 'Identificación de la entidad',
      fields: [
        { path: 'entityType', label: 'Tipo de entidad', kind: 'text' },
        { path: 'entityDetail', label: 'Detalle del tipo de entidad', kind: 'text' },
        {
          path: 'entityOther',
          label: 'Otro tipo de entidad',
          portalLabel: 'Especifique el otro tipo de entidad',
          kind: 'text'
        },
        { path: 'companyName', label: 'Razón social', kind: 'text' },
        { path: 'tradeName', label: 'Nombre comercial', kind: 'text' },
        { path: 'nit', label: 'NIT', kind: 'text' },
        { path: 'registrationDate', label: 'Fecha de inscripción', kind: 'date' },
        {
          path: 'jurisdiction',
          label: 'Jurisdicción',
          portalLabel: 'Jurisdicción / país de constitución',
          kind: 'country'
        },
        {
          path: 'societyFormation',
          label: '¿Sociedad en formación?',
          portalLabel: '¿La entidad es una sociedad en formación?',
          kind: 'boolean'
        }
      ]
    },
    {
      title: 'Actividad y contacto',
      fields: [
        {
          path: 'economicActivity',
          label: 'Actividad económica',
          portalLabel: 'Actividad económica específica',
          kind: 'text'
        },
        { path: 'economicActivityRTU', label: 'Actividad económica (RTU)', kind: 'text' },
        {
          path: 'cpe',
          label: '¿Contratista del Estado?',
          portalLabel: '¿Es contratista o proveedor del Estado?',
          kind: 'boolean'
        },
        {
          path: 'email',
          label: 'Correos',
          portalLabel: 'Correos electrónicos para correspondencia',
          kind: 'text'
        },
        { path: 'website', label: 'Sitio web', kind: 'text' }
      ]
    },
    {
      title: 'Domicilio fiscal',
      fields: [
        { path: 'address.country', label: 'País', kind: 'country' },
        { path: 'address.address', label: 'Dirección', kind: 'text' },
        { path: 'address.zone', label: 'Zona', kind: 'number' }
      ]
    },
    {
      // Sociedad mercantil (entidad lucrativa). Sólo tiene datos si el tipo es lucrativo.
      title: 'Sociedad mercantil',
      fields: [
        {
          path: 'commercialSociety.publicDeed.number',
          label: 'Escritura · Número',
          portalLabel: 'Número de escritura',
          kind: 'text'
        },
        {
          path: 'commercialSociety.publicDeed.date',
          label: 'Escritura · Fecha',
          portalLabel: 'Fecha de la escritura',
          kind: 'date'
        },
        {
          path: 'commercialSociety.publicDeed.notary',
          label: 'Escritura · Notario',
          portalLabel: 'Notario que la autorizó',
          kind: 'text'
        },
        {
          path: 'commercialSociety.companyPatent.number',
          label: 'Patente sociedad · Número',
          portalLabel: 'Número de registro',
          kind: 'text'
        },
        {
          path: 'commercialSociety.companyPatent.sheet',
          label: 'Patente sociedad · Folio',
          portalLabel: 'Folio',
          kind: 'text'
        },
        {
          path: 'commercialSociety.companyPatent.book',
          label: 'Patente sociedad · Libro',
          portalLabel: 'Libro',
          kind: 'text'
        },
        {
          path: 'commercialSociety.commercialPatent.number',
          label: 'Patente comercio · Número',
          portalLabel: 'Número de registro',
          kind: 'text'
        },
        {
          path: 'commercialSociety.commercialPatent.sheet',
          label: 'Patente comercio · Folio',
          portalLabel: 'Folio',
          kind: 'text'
        },
        {
          path: 'commercialSociety.commercialPatent.book',
          label: 'Patente comercio · Libro',
          portalLabel: 'Libro',
          kind: 'text'
        }
      ]
    },
    {
      // Otro tipo de sociedad (no lucrativa / organismo). Sólo tiene datos para B/C.
      title: 'Otro tipo de sociedad',
      fields: [
        {
          path: 'otherSociety.creationDocument.number',
          label: 'Documento de creación · Número',
          portalLabel: 'Número del documento',
          kind: 'text'
        },
        {
          path: 'otherSociety.creationDocument.date',
          label: 'Documento de creación · Fecha',
          portalLabel: 'Fecha del documento',
          kind: 'date'
        },
        {
          path: 'otherSociety.creationDocument.authorizer',
          label: 'Documento de creación · Autoriza',
          portalLabel: 'Nombre de quién autoriza',
          kind: 'text'
        },
        {
          path: 'otherSociety.registrationData.number',
          label: 'Registro · Número',
          portalLabel: 'Número',
          kind: 'text'
        },
        {
          path: 'otherSociety.registrationData.sheet',
          label: 'Registro · Folio',
          portalLabel: 'Folio',
          kind: 'text'
        },
        {
          path: 'otherSociety.registrationData.book',
          label: 'Registro · Libro',
          portalLabel: 'Libro',
          kind: 'text'
        },
        {
          path: 'otherSociety.registrationData.institution',
          label: 'Registro · Institución',
          portalLabel: 'Institución donde se registró',
          kind: 'text'
        }
      ]
    },
    {
      title: 'Estructura accionaria',
      fields: [
        {
          path: 'shareholding',
          label: '¿Posee accionistas al 10%+?',
          portalLabel:
            '¿El cliente posee accionistas, socios o asociados con el 10% o más de participación o acciones bajo su control?',
          kind: 'boolean'
        }
      ]
    }
  ],
  arrays: [
    {
      path: 'shareholdingStructure.individual',
      itemTitle: 'Accionista individual',
      fields: [
        { path: 'name.first', label: 'Nombre', kind: 'fullName' },
        { path: 'lastName.first', label: 'Apellido', kind: 'fullName' },
        { path: 'participation', label: 'Participación (%)', kind: 'number' },
        { path: 'foreign', label: '¿Extranjero?', portalLabel: '¿Es extranjero?', kind: 'boolean' }
      ]
    },
    {
      path: 'shareholdingStructure.juridical',
      itemTitle: 'Accionista jurídico',
      fields: [
        {
          path: 'name',
          label: 'Razón social',
          portalLabel: 'Nombre, razón o denominación social',
          kind: 'text'
        },
        { path: 'participation', label: 'Participación (%)', kind: 'number' },
        { path: 'foreign', label: '¿Extranjera?', portalLabel: '¿Es extranjera?', kind: 'boolean' }
      ]
    },
    {
      path: 'membersOfBoard',
      itemTitle: 'Miembro del órgano superior',
      fields: [
        { path: 'name.first', label: 'Nombre', kind: 'fullName' },
        { path: 'lastName.first', label: 'Apellido', kind: 'fullName' },
        {
          path: 'identificationCard.number',
          label: 'Documento',
          portalLabel: 'Número de documento',
          kind: 'text'
        },
        { path: 'position', label: 'Cargo', portalLabel: 'Cargo que ocupa', kind: 'text' },
        { path: 'foreign', label: '¿Extranjero?', portalLabel: '¿Es extranjero?', kind: 'boolean' },
        // Declaración PEP del miembro (resumen). El detalle de cada registro PEP anidado
        // (institución/puesto por persona) no lo renderiza el review plano — pendiente.
        { path: 'isPep', label: '¿Es PEP?', kind: 'boolean' },
        { path: 'hasRelativesPep', label: '¿Familiares PEP?', kind: 'boolean' },
        { path: 'hasPartnersPep', label: '¿Socios/asociados PEP?', kind: 'boolean' },
        { path: 'isCPE', label: '¿Contratista del Estado?', kind: 'boolean' }
      ]
    },
    {
      // Modificaciones a la escritura pública (sociedad mercantil, entidad lucrativa).
      path: 'commercialSociety.modificationPublicDeed',
      itemTitle: 'Modificación de escritura',
      fields: [
        { path: 'number', label: 'Número', portalLabel: 'Número de escritura', kind: 'text' },
        { path: 'date', label: 'Fecha', kind: 'date' },
        { path: 'notary', label: 'Notario', kind: 'text' },
        { path: 'typeModification', label: 'Tipo de modificación', kind: 'modification' }
      ]
    },
    {
      // Modificaciones al documento de creación (otro tipo de sociedad).
      path: 'otherSociety.modificationCreationDocument',
      itemTitle: 'Modificación de documento',
      fields: [
        { path: 'number', label: 'Número', portalLabel: 'Número del documento', kind: 'text' },
        { path: 'date', label: 'Fecha', kind: 'date' },
        {
          path: 'authorizer',
          label: 'Autoriza',
          portalLabel: 'Nombre de quién autoriza',
          kind: 'text'
        },
        { path: 'typeModification', label: 'Tipo de modificación', kind: 'modification' }
      ]
    }
  ]
}

/** Per-section field metadata. Sections without entries (e.g. attachments) are slot-based. */
export const SECTION_FIELDS: Partial<Record<PortalSectionId, SectionFields>> = {
  applicant: APPLICANT_FIELDS,
  company: COMPANY_FIELDS,
  pep: PEP_FIELDS,
  economic: ECONOMIC_FIELDS,
  equity: EQUITY_FIELDS,
  legal_representatives: LEGAL_REPRESENTATIVES_FIELDS
}

/**
 * Per-client-type OVERRIDES: a section whose field set is a DIFFERENT model per person
 * type (not just a subset). `economic` for juridical is an entirely different catalog —
 * it cannot be derived by pruning `SECTION_FIELDS.economic`. Sections that only differ
 * by a few fields use `clientTypes` pruning instead (e.g. equity spouse).
 */
const SECTION_FIELDS_BY_CLIENT_TYPE: Partial<
  Record<PortalClientType, Partial<Record<PortalSectionId, SectionFields>>>
> = {
  juridical: {
    economic: ECONOMIC_JURIDICAL_FIELDS
  }
}

/** True if a descriptor applies to the given person type (no `clientTypes` = all). */
function appliesTo(field: FieldDescriptor, clientType: PortalClientType): boolean {
  return field.clientTypes == null || field.clientTypes.includes(clientType)
}

/** True if a descriptor is shown on the given surface (no `audiences` = both). */
function shownTo(field: FieldDescriptor, audience: FieldAudience): boolean {
  return field.audiences == null || field.audiences.includes(audience)
}

/** Prune a section's descriptors, dropping groups and arrays left empty. */
function prune(
  fields: SectionFields,
  clientType: PortalClientType | undefined,
  audience: FieldAudience | undefined
): SectionFields {
  const keep = (f: FieldDescriptor) =>
    (clientType == null || appliesTo(f, clientType)) && (audience == null || shownTo(f, audience))
  const scalarGroups = (fields.scalarGroups ?? [])
    .map((g) => ({ ...g, fields: g.fields.filter(keep) }))
    .filter((g) => g.fields.length > 0)
  const arrays = (fields.arrays ?? [])
    .map((a) => ({ ...a, fields: a.fields.filter(keep) }))
    .filter((a) => a.fields.length > 0)
  return {
    ...(fields.scalarGroups ? { scalarGroups } : {}),
    ...(fields.arrays ? { arrays } : {})
  }
}

/**
 * Field metadata for a section (or `undefined` for slot-based sections).
 *
 * Both filters are opt-in and independent:
 *  - `clientType` drops what does not apply to that person type (equity spouse → individual).
 *  - `audience` drops what that surface must not show (`['internal']` fields never reach the
 *    public portal). Omitting it returns everything — which is what review wants.
 */
export function getSectionFields(
  id: PortalSectionId,
  clientType?: PortalClientType,
  audience?: FieldAudience
): SectionFields | undefined {
  // A full per-client-type override wins (e.g. juridical economic = different catalog)…
  const override = clientType ? SECTION_FIELDS_BY_CLIENT_TYPE[clientType]?.[id] : undefined
  const fields = override ?? SECTION_FIELDS[id]
  if (!fields) return undefined
  // …pero igual se poda. Antes el override salía por atajo y se saltaba el filtro entero:
  // un campo con `clientTypes`/`audiences` dentro de la económica jurídica no se respetaba.
  if (clientType == null && audience == null) return fields
  return prune(fields, clientType, audience)
}

/** Flatten all scalar + array-item descriptors of a section (for iteration/labels). */
export function flattenSectionFields(fields: SectionFields): FieldDescriptor[] {
  const scalars = (fields.scalarGroups ?? []).flatMap((g) => g.fields)
  const arrays = (fields.arrays ?? []).flatMap((a) => a.fields)
  return [...scalars, ...arrays]
}

/**
 * Descriptors of a section indexed by dot-path, so a surface can ask "what is this field
 * called?" without scanning the catalog on every render.
 *
 * KEY SCHEME. Scalars are keyed by their own path (`residence.address`). An array's item
 * fields are keyed by ARRAY PATH + ITEM PATH (`assets.banks` + `name` → `assets.banks.name`),
 * WITHOUT the row index: the metadata describes the column, not the row, so the caller
 * looks up `assets.banks.name` while RHF names the input `assets.banks.3.name`.
 *
 * Pure and framework-free on purpose — resolving `portalLabel ?? label` and deciding what
 * to do about a path that does not exist are the surface's calls, not the dictionary's.
 */
export function sectionFieldIndex(
  id: PortalSectionId,
  clientType?: PortalClientType,
  audience?: FieldAudience
): ReadonlyMap<string, FieldDescriptor> {
  const index = new Map<string, FieldDescriptor>()
  const fields = getSectionFields(id, clientType, audience)
  if (!fields) return index

  for (const group of fields.scalarGroups ?? []) {
    for (const field of group.fields) index.set(field.path, field)
  }
  for (const array of fields.arrays ?? []) {
    for (const field of array.fields) index.set(`${array.path}.${field.path}`, field)
  }
  return index
}
